import { Organization, OrgHat, OrgSecurityGroup, OrgPolicy, OrgUser, OrgFeature, DisplayValue } from '@newgenus/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass, NgStyle } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { OrgEntityColorsService } from '../../../org-dashboard/services/org-entity-colors.service';

// ───────────── Interfaces ─────────────

export type ItemSelectorType =
    | 'positions'
    | 'positions-with-hats'
    | 'security-groups'
    | 'policies'
    | 'users'
    | 'features';

export interface ItemSelectorDialogInput {
    organization: Organization;
    type: ItemSelectorType;
    selectedKeys?: string[];
    title?: string;
    subtitle?: string;
    /** Pre-selected permissions per feature key (features type only) */
    permissionSelections?: Record<string, string[]>;
    /** Keys currently in pending approval state (users type only) */
    pendingKeys?: string[];
    /** Keys that are already approved/assigned — used to show newly ticked users as pending */
    approvedKeys?: string[];
}

export interface ItemSelectorDialogResult {
    selectedKeys: string[];
    selectedItems: DisplayValue[];
    /** Selected permissions per feature key (features type only) */
    permissionSelections?: Record<string, string[]>;
}

/** Tree node for the positions tree view */
export interface SelectorTreeNode {
    key: string;
    name: string;
    partType: string;
    level: number;
    children: SelectorTreeNode[];
    isSelected: boolean;
    parentKey?: string;
    hatCount: number;
    userCount: number;
    /** True if this node represents a hat, not a position */
    isHatNode?: boolean;
    /** For hat nodes: the parent position key */
    positionKey?: string;
    /** For hat nodes: security group names for display */
    securityGroupNames?: string[];
    /** Whether the parent position is selected (controls hat selectability) */
    isParentSelected?: boolean;
}

/** Feature detail for expandable SG items */
export interface SelectorFeatureDetail {
    key: string;
    name: string;
    description?: string;
    permissions: string[];
    permissionDescriptions?: Record<string, string>;
    enabled: boolean;
    /** Tracks which permissions are selected (features type only) */
    selectedPermissions?: string[];
}

/** Flat item for list-based views (SGs, Policies, Users) */
interface SelectableListItem {
    key: string;
    name: string;
    description?: string;
    subtitle: string;
    icon: string;
    isSelected: boolean;
    isExpanded: boolean;
    badge?: string;
    badgeColor?: string;
    /** Feature details (only for security-groups type) */
    features?: SelectorFeatureDetail[];
}

// ───────────── Type Configuration ─────────────

interface TypeConfig {
    icon: string;
    title: string;
    subtitle: string;
    searchPlaceholder: string;
    emptyMessage: string;
    emptySubmessage: string;
    headerClass: string;
}

const TYPE_CONFIGS: Record<ItemSelectorType, TypeConfig> = {
    positions: {
        icon: 'account_tree',
        title: 'Select Org Parts',
        subtitle: 'Choose org parts from the organization structure',
        searchPlaceholder: 'Search org parts...',
        emptyMessage: 'No org parts available',
        emptySubmessage: 'The organization has no parts defined yet.',
        headerClass: 'header-positions',
    },
    'positions-with-hats': {
        icon: 'account_tree',
        title: 'Select Org Parts & Hats',
        subtitle: 'Choose org parts and their hats to assign',
        searchPlaceholder: 'Search org parts or hats...',
        emptyMessage: 'No org parts available',
        emptySubmessage: 'The organization has no parts defined yet.',
        headerClass: 'header-positions',
    },
    'security-groups': {
        icon: 'security',
        title: 'Select Security Groups',
        subtitle: 'Choose security groups to assign',
        searchPlaceholder: 'Search security groups...',
        emptyMessage: 'No security groups available',
        emptySubmessage: 'Create security groups from the organization board.',
        headerClass: 'header-security-groups',
    },
    policies: {
        icon: 'policy',
        title: 'Select Policies',
        subtitle: 'Choose policies to link with this hat',
        searchPlaceholder: 'Search policies...',
        emptyMessage: 'No policies available',
        emptySubmessage: 'Create policies from the organization board.',
        headerClass: 'header-policies',
    },
    users: {
        icon: 'people',
        title: 'Select Staff',
        subtitle: 'Choose staff members to assign',
        searchPlaceholder: 'Search staff by name or email...',
        emptyMessage: 'No staff available',
        emptySubmessage: 'Invite staff to the organization first.',
        headerClass: 'header-users',
    },
    features: {
        icon: 'extension',
        title: 'Select Features',
        subtitle: 'Choose features to include in this security group',
        searchPlaceholder: 'Search features...',
        emptyMessage: 'No features available',
        emptySubmessage: 'Create features from the organization board.',
        headerClass: 'header-features',
    },
};

// ───────────── Component ─────────────

@Component({
    selector: 'shared-org-item-selector-dialog',
    imports: [
        NgClass,
        NgStyle,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatTreeModule,
        MatTooltipModule,
        MatProgressBarModule,
        MatTabsModule,
    ],
    templateUrl: './org-item-selector-dialog.html',
    styleUrls: ['./org-item-selector-dialog.scss'],
})
export class OrgItemSelectorDialogComponent implements OnInit {
    // ── Entity Colors ──
    public entityColors = inject(OrgEntityColorsService);

    // ── Config ──
    public config: TypeConfig;

    // ── Search ──
    public searchTerm = '';

    // ── Positions (tree mode) ──
    public treeControl = new NestedTreeControl<SelectorTreeNode>((node) => node.children);
    public treeDataSource = new MatTreeNestedDataSource<SelectorTreeNode>();
    private orgTreeData: SelectorTreeNode[] = [];
    /** Map of node key → original node reference for syncing selections from filtered clones */
    private nodeMap = new Map<string, SelectorTreeNode>();

    // ── Position-free hats (hats not on any position, e.g. system feature hats) ──
    public positionFreeHats: SelectorTreeNode[] = [];

    // ── Tab index for positions-with-hats mode ──
    public hatTabIndex = 0;

    // ── Posts-only filter (tree mode) ──
    public postsOnlyFilter = false;

    // ── List mode (SGs, Policies, Users) ──
    public listItems: SelectableListItem[] = [];
    public filteredListItems: SelectableListItem[] = [];

    // ── Summary ──
    public selectedCount = 0;
    public totalCount = 0;

    // ── Part type icons (reuse from policy target selector) ──
    private partTypeIcons: Record<string, string> = {
        executive: 'stars',
        division: 'account_tree',
        department: 'folder',
        section: 'workspaces',
        unit: 'widgets',
        post: 'person',
    };

    constructor(
        public dialogRef: MatDialogRef<OrgItemSelectorDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ItemSelectorDialogInput
    ) {
        this.config = TYPE_CONFIGS[data.type];
        // Apply overrides
        if (data.title) this.config = { ...this.config, title: data.title };
        if (data.subtitle) this.config = { ...this.config, subtitle: data.subtitle };
    }

    // ═══════════════ Lifecycle ═══════════════

    public ngOnInit(): void {
        if (this.data.type === 'positions' || this.data.type === 'positions-with-hats') {
            this.buildPositionsTree();
        } else {
            this.buildListItems();
        }
        this.updateCounts();
    }

    // ═══════════════ Tree Mode (Positions) ═══════════════

    private buildPositionsTree(): void {
        const orgParts = this.data.organization?.orgParts || [];
        const preSelected = new Set(this.data.selectedKeys || []);
        const includeHats = this.data.type === 'positions-with-hats';

        // Build node map
        const nodeMap = new Map<string, SelectorTreeNode>();
        const rootNodes: SelectorTreeNode[] = [];

        for (const part of orgParts) {
            const partKey = part.partKey || part.key || '';
            const node: SelectorTreeNode = {
                key: partKey,
                name: part.name || part.mainTitle || partKey,
                partType: part.partType || this.inferPartType(part.depth || 0),
                level: part.depth || 0,
                children: [],
                isSelected: preSelected.has(partKey),
                parentKey: part.parentPartKey,
                userCount: this.countUsersInPart(partKey),
                hatCount: this.countHatsInPart(partKey),
            };
            nodeMap.set(partKey, node);
        }

        // Build hierarchy
        for (const part of orgParts) {
            const partKey = part.partKey || part.key || '';
            const node = nodeMap.get(partKey);
            if (!node) continue;

            const parent = part.parentPartKey ? nodeMap.get(part.parentPartKey) : null;
            if (parent) {
                parent.children.push(node);
            } else {
                rootNodes.push(node);
            }
        }

        // In positions-with-hats mode, add hat nodes as children of their position
        // and collect position-free hats separately
        if (includeHats) {
            this.addHatNodesToTree(nodeMap, preSelected);
            this.collectPositionFreeHats(nodeMap, preSelected);
        }

        this.sortNodesRecursively(rootNodes);
        this.orgTreeData = rootNodes;
        this.treeDataSource.data = this.orgTreeData;
        this.totalCount = orgParts.length + this.positionFreeHats.length;

        // Populate the class-level nodeMap for syncing selections from search-filtered clones
        this.nodeMap.clear();
        this.registerNodesRecursively(this.orgTreeData);

        // Expand first level only (collapsed by default)
        this.expandToLevel(this.orgTreeData, 1);
    }

    /**
     * Add hat nodes as children of their respective position nodes.
     * Hats are only selectable when their parent position is selected.
     */
    private addHatNodesToTree(nodeMap: Map<string, SelectorTreeNode>, preSelected: Set<string>): void {
        const orgHats = this.data.organization?.orgHats;
        if (!orgHats?.hatsKeys) return;

        for (const hatKey of orgHats.hatsKeys) {
            const hat = orgHats[hatKey] as OrgHat;
            if (!hat || hat.isSoftDeleted) continue;

            const positions = hat.positionArray || [];
            for (const posKey of positions) {
                const posNode = nodeMap.get(posKey);
                if (!posNode) continue;

                const sgNames = (hat.securityGroupsArray || [])
                    .map((sg) => (typeof sg === 'string' ? sg : sg?.name))
                    .filter(Boolean);

                const hatNode: SelectorTreeNode = {
                    key: hatKey,
                    name: hat.name || hatKey,
                    partType: 'hat',
                    level: posNode.level + 1,
                    children: [],
                    isSelected: preSelected.has(hatKey),
                    parentKey: posKey,
                    hatCount: 0,
                    userCount: hat.assignedToArray?.length || 0,
                    isHatNode: true,
                    positionKey: posKey,
                    securityGroupNames: sgNames,
                    isParentSelected: posNode.isSelected,
                };

                posNode.children.push(hatNode);
            }
        }
    }

    /**
     * Collect hats that have no positions (empty positionArray).
     * These are organization-level hats (e.g. system feature hats) that can be
     * assigned to any user without requiring a position.
     */
    private collectPositionFreeHats(nodeMap: Map<string, SelectorTreeNode>, preSelected: Set<string>): void {
        const orgHats = this.data.organization?.orgHats;
        if (!orgHats?.hatsKeys) return;

        this.positionFreeHats = [];
        for (const hatKey of orgHats.hatsKeys) {
            const hat = orgHats[hatKey] as OrgHat;
            if (!hat || hat.isSoftDeleted) continue;

            const positions = hat.positionArray || [];
            if (positions.length > 0) continue; // skip hats that have positions

            const sgNames = (hat.securityGroupsArray || [])
                .map((sg) => (typeof sg === 'string' ? sg : sg?.name))
                .filter(Boolean);

            this.positionFreeHats.push({
                key: hatKey,
                name: hat.name || hatKey,
                partType: 'hat',
                level: 0,
                children: [],
                isSelected: preSelected.has(hatKey),
                hatCount: 0,
                userCount: hat.assignedToArray?.length || 0,
                isHatNode: true,
                securityGroupNames: sgNames,
                isParentSelected: true, // always selectable
            });
        }

        this.positionFreeHats.sort((a, b) => a.name.localeCompare(b.name));
    }

    /** Toggle a position-free hat's selection */
    public togglePositionFreeHat(hat: SelectorTreeNode): void {
        hat.isSelected = !hat.isSelected;
        this.updateCounts();
    }

    /** Clear all position-free hat selections */
    public clearPositionFreeHatSelections(): void {
        for (const hat of this.positionFreeHats) {
            hat.isSelected = false;
        }
    }

    private sortNodesRecursively(nodes: SelectorTreeNode[]): void {
        // Sort: positions first (alphabetically), then hats (alphabetically)
        nodes.sort((a, b) => {
            if (a.isHatNode !== b.isHatNode) return a.isHatNode ? 1 : -1;
            return a.name.localeCompare(b.name);
        });
        for (const node of nodes) {
            if (node.children.length > 0) {
                this.sortNodesRecursively(node.children);
            }
        }
    }

    private expandToLevel(nodes: SelectorTreeNode[], maxLevel: number, currentLevel = 0): void {
        if (currentLevel >= maxLevel) return;
        for (const node of nodes) {
            this.treeControl.expand(node);
            if (node.children.length > 0) {
                this.expandToLevel(node.children, maxLevel, currentLevel + 1);
            }
        }
    }

    /** Register all nodes (including hat children) in the class-level nodeMap */
    private registerNodesRecursively(nodes: SelectorTreeNode[]): void {
        for (const node of nodes) {
            this.nodeMap.set(node.key, node);
            if (node.children.length > 0) {
                this.registerNodesRecursively(node.children);
            }
        }
    }

    private inferPartType(level: number): string {
        const types = ['executive', 'division', 'department', 'section', 'unit', 'post'];
        return types[Math.min(level, types.length - 1)];
    }

    private countUsersInPart(partKey: string): number {
        const orgHats = this.data.organization?.orgHats;
        let count = 0;
        if (orgHats?.hatsKeys) {
            for (const hatKey of orgHats.hatsKeys) {
                const hat = orgHats[hatKey] as OrgHat;
                if (hat && hat.positionArray?.includes(partKey)) {
                    count += hat.assignedToArray?.length || hat.userKeys?.length || 0;
                }
            }
        }
        return count;
    }

    private countHatsInPart(partKey: string): number {
        const orgHats = this.data.organization?.orgHats;
        let count = 0;
        if (orgHats?.hatsKeys) {
            for (const hatKey of orgHats.hatsKeys) {
                const hat = orgHats[hatKey] as OrgHat;
                if (hat && hat.positionArray?.includes(partKey)) {
                    count++;
                }
            }
        }
        return count;
    }

    /** Check if a tree node is selectable (only posts and hats are selectable in positions mode) */
    public isNodeSelectable(node: SelectorTreeNode): boolean {
        if (node.isHatNode) return node.isParentSelected !== false;
        return node.partType === 'post';
    }

    /** Toggle posts-only filter — shows only post nodes in the tree */
    public togglePostsFilter(): void {
        this.postsOnlyFilter = !this.postsOnlyFilter;
        this.applySearch();
    }

    /** Toggle a tree node's selection */
    public toggleTreeNode(node: SelectorTreeNode): void {
        // Hat nodes can only be selected if their parent position is selected
        if (node.isHatNode && !node.isParentSelected) {
            return;
        }

        // Only posts (and hats) are selectable
        if (!node.isHatNode && node.partType !== 'post') {
            return;
        }

        node.isSelected = !node.isSelected;

        // Sync selection to the original node in orgTreeData (search filter creates clones)
        const original = this.nodeMap.get(node.key);
        if (original && original !== node) {
            original.isSelected = node.isSelected;
        }

        // In positions-with-hats mode, when a position is toggled,
        // update isParentSelected on all child hat nodes
        if (this.data.type === 'positions-with-hats' && !node.isHatNode) {
            this.updateHatChildrenState(node);
            // Also update on the original node's children
            if (original && original !== node) {
                this.updateHatChildrenState(original);
            }
        }

        this.updateCounts();
    }

    /**
     * When a position is selected/deselected, update hat children accordingly.
     * If position is deselected, deselect all its hat children.
     */
    private updateHatChildrenState(positionNode: SelectorTreeNode): void {
        for (const child of positionNode.children) {
            if (child.isHatNode) {
                child.isParentSelected = positionNode.isSelected;
                // If position is deselected, deselect all hats
                if (!positionNode.isSelected) {
                    child.isSelected = false;
                }
            }
            // Recurse for deeply nested positions (a position can contain positions)
            if (!child.isHatNode && child.children.length > 0) {
                // don't cascade position selection - only direct hat children
            }
        }
    }

    /** Quick-select all nodes of a specific part type */
    public selectAllOfType(partType: string): void {
        this.setNodesByType(this.orgTreeData, partType, true);
        this.updateCounts();
    }

    /** Clear all tree selections */
    public clearTreeSelections(): void {
        this.clearNodesRecursively(this.orgTreeData);
        this.clearPositionFreeHatSelections();
        this.updateCounts();
    }

    private setNodesByType(nodes: SelectorTreeNode[], partType: string, selected: boolean): void {
        for (const node of nodes) {
            if (node.partType === partType) {
                node.isSelected = selected;
            }
            if (node.children.length > 0) {
                this.setNodesByType(node.children, partType, selected);
            }
        }
    }

    private clearNodesRecursively(nodes: SelectorTreeNode[]): void {
        for (const node of nodes) {
            node.isSelected = false;
            if (node.children.length > 0) {
                this.clearNodesRecursively(node.children);
            }
        }
    }

    /** Collect all selected keys from tree */
    private getSelectedTreeKeys(): string[] {
        const keys: string[] = [];
        this.collectSelectedKeys(this.orgTreeData, keys);
        return keys;
    }

    private collectSelectedKeys(nodes: SelectorTreeNode[], keys: string[]): void {
        for (const node of nodes) {
            if (node.isSelected) {
                keys.push(node.key);
            }
            if (node.children.length > 0) {
                this.collectSelectedKeys(node.children, keys);
            }
        }
    }

    /** Count selected in tree */
    private countSelectedTreeNodes(): number {
        const treeCount = this.getSelectedTreeKeys().length;
        const freeHatCount = this.positionFreeHats.filter((h) => h.isSelected).length;
        return treeCount + freeHatCount;
    }

    // ── Tree template helpers ──

    public hasChildren = (_: number, node: SelectorTreeNode): boolean => {
        return node.children && node.children.length > 0;
    };

    public getPartIcon(partType: string): string {
        return this.partTypeIcons[partType] || 'folder';
    }

    // ═══════════════ List Mode (SGs, Policies, Users) ═══════════════

    private buildListItems(): void {
        const org = this.data.organization;
        const preSelected = new Set(this.data.selectedKeys || []);

        switch (this.data.type) {
            case 'security-groups':
                this.buildSecurityGroupItems(org, preSelected);
                break;
            case 'policies':
                this.buildPolicyItems(org, preSelected);
                break;
            case 'users':
                this.buildUserItems(org, preSelected);
                break;
            case 'features':
                this.buildFeatureItems(org, preSelected);
                break;
        }

        // Sort: selected first, then alphabetical
        this.listItems.sort((a, b) => {
            if (a.isSelected !== b.isSelected) return a.isSelected ? -1 : 1;
            return a.name.localeCompare(b.name);
        });
        this.filteredListItems = [...this.listItems];
        this.totalCount = this.listItems.length;
    }

    private buildSecurityGroupItems(org: Organization, preSelected: Set<string>): void {
        const sgKeys = org?.orgSecurityGroups?.securityGroupsKeys || [];
        for (const key of sgKeys) {
            const sg = org.orgSecurityGroups[key] as OrgSecurityGroup;
            if (!sg) continue;

            // Extract feature details for expandable view
            const features: SelectorFeatureDetail[] = [];
            if (sg.featureDetails) {
                for (const fKey of Object.keys(sg.featureDetails)) {
                    const f = sg.featureDetails[fKey];
                    if (f) {
                        features.push({
                            key: f.key || fKey,
                            name: f.name || fKey,
                            permissions: f.permissions || [],
                            enabled: f.enabled ?? true,
                        });
                    }
                }
            }
            features.sort((a, b) => a.name.localeCompare(b.name));

            const featureCount = features.length;
            const totalPerms = features.reduce((sum, f) => sum + f.permissions.length, 0);

            this.listItems.push({
                key,
                name: sg.name || key,
                subtitle:
                    featureCount > 0
                        ? `${featureCount} feature${featureCount > 1 ? 's' : ''} · ${totalPerms} permission${totalPerms !== 1 ? 's' : ''}`
                        : 'No features',
                icon: 'security',
                isSelected: preSelected.has(key),
                isExpanded: false,
                features,
            });
        }
    }

    private buildPolicyItems(org: Organization, preSelected: Set<string>): void {
        const policyKeys = org?.orgPolicies?.policiesKeys || [];
        for (const key of policyKeys) {
            const policy = org.orgPolicies[key] as OrgPolicy;
            if (!policy) continue;

            const status = policy.status || 'draft';
            const category = policy.category || '';
            const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);

            this.listItems.push({
                key,
                name: policy.name || key,
                subtitle: [category, statusLabel].filter(Boolean).join(' · '),
                icon: 'policy',
                isSelected: preSelected.has(key),
                isExpanded: false,
                badge: statusLabel,
                badgeColor: this.getPolicyStatusColor(status),
            });
        }
    }

    private buildUserItems(org: Organization, preSelected: Set<string>): void {
        const userKeys = org?.orgUsers?.userKeys || [];
        const pendingSet = new Set(this.data.pendingKeys || []);
        for (const key of userKeys) {
            const user = org.orgUsers[key] as OrgUser;
            if (!user) continue;
            const isPending = pendingSet.has(key);
            this.listItems.push({
                key,
                name: user.name || user.userName || key,
                subtitle: user.email || '',
                icon: 'person',
                isSelected: preSelected.has(key),
                isExpanded: false,
                badge: isPending ? 'Pending' : undefined,
                badgeColor: isPending ? '#e65100' : undefined,
            });
        }
    }

    private buildFeatureItems(org: Organization, preSelected: Set<string>): void {
        const featureKeys = org?.orgFeatures?.featuresKeys || [];
        for (const key of featureKeys) {
            const feature = org.orgFeatures?.[key] as OrgFeature;
            if (!feature || feature.systemFeature) continue;

            const perms = feature.permissions || [];
            const permCount = perms.length;
            const subtitle =
                permCount > 0 ? `${permCount} permission${permCount !== 1 ? 's' : ''}` : 'No permissions defined';

            // Build a single-item features array so the expand/collapse UI
            // shows available permissions (same pattern as SG items)
            const preSelectedPerms = this.data.permissionSelections?.[key] || [];
            const featureDetails: SelectorFeatureDetail[] =
                permCount > 0
                    ? [
                          {
                              key: feature.key || key,
                              name: feature.name || key,
                              description: feature.description || '',
                              permissions: perms,
                              permissionDescriptions: (feature as any).permissionDescriptions || {},
                              enabled: feature.enabled !== false,
                              selectedPermissions: preSelectedPerms.filter((p) => perms.includes(p)),
                          },
                      ]
                    : [];

            this.listItems.push({
                key,
                name: feature.name || key,
                description: feature.description || '',
                subtitle,
                icon: 'extension',
                isSelected: preSelected.has(key),
                isExpanded: false,
                badge: feature.enabled !== false ? 'Enabled' : 'Disabled',
                badgeColor: feature.enabled !== false ? '#4caf50' : '#9e9e9e',
                features: featureDetails,
            });
        }
    }

    private getPolicyStatusColor(status: string): string {
        switch (status) {
            case 'published':
                return '#4caf50';
            case 'draft':
                return '#ff9800';
            case 'archived':
                return '#9e9e9e';
            case 'revoked':
                return '#f44336';
            default:
                return '#666';
        }
    }

    /** Toggle a list item's selection */
    public toggleListItem(item: SelectableListItem): void {
        item.isSelected = !item.isSelected;

        // In features mode, auto-expand when selecting a feature that has permissions
        if (this.isFeaturesMode && item.isSelected && item.features && item.features.length > 0) {
            item.isExpanded = true;
        }

        this.refreshPendingBadges();
        this.updateCounts();
    }

    /** Update pending badges on user items based on approval state */
    private refreshPendingBadges(): void {
        if (this.data.type !== 'users' || !this.data.approvedKeys) return;
        const approvedSet = new Set(this.data.approvedKeys);
        for (const item of this.listItems) {
            if (item.isSelected && !approvedSet.has(item.key)) {
                item.badge = 'Pending';
                item.badgeColor = '#e65100';
            } else if (!approvedSet.has(item.key)) {
                item.badge = undefined;
                item.badgeColor = undefined;
            }
            // If it's an approved user, leave badge as-is (no badge)
        }
    }

    /** Toggle expand/collapse for items with details (e.g. SG features) */
    public toggleExpand(item: SelectableListItem, event: Event): void {
        event.stopPropagation();
        item.isExpanded = !item.isExpanded;
    }

    /** Toggle a permission on/off within a feature (features type only) */
    public togglePermission(feature: SelectorFeatureDetail, perm: string, event: Event): void {
        event.stopPropagation();
        if (!feature.selectedPermissions) {
            feature.selectedPermissions = [];
        }
        const idx = feature.selectedPermissions.indexOf(perm);
        if (idx >= 0) {
            feature.selectedPermissions.splice(idx, 1);
        } else {
            feature.selectedPermissions.push(perm);
        }
    }

    /** Select all permissions for a feature */
    public selectAllPermissions(feature: SelectorFeatureDetail, event: Event): void {
        event.stopPropagation();
        feature.selectedPermissions = [...feature.permissions];
    }

    /** Clear all permission selections for a feature */
    public clearAllPermissions(feature: SelectorFeatureDetail, event: Event): void {
        event.stopPropagation();
        feature.selectedPermissions = [];
    }

    /** Check if we're in features mode (for template) */
    public get isFeaturesMode(): boolean {
        return this.data.type === 'features';
    }

    /** Select all visible list items */
    public selectAllList(): void {
        for (const item of this.filteredListItems) {
            item.isSelected = true;
        }
        this.refreshPendingBadges();
        this.updateCounts();
    }

    /** Clear all list selections */
    public clearAllList(): void {
        for (const item of this.listItems) {
            item.isSelected = false;
        }
        this.refreshPendingBadges();
        this.updateCounts();
    }

    // ═══════════════ Search ═══════════════

    public applySearch(): void {
        if (this.isTreeMode) {
            this.applyTreeSearch();
        } else {
            this.applyListSearch();
        }
    }

    public clearSearch(): void {
        this.searchTerm = '';
        if (this.isTreeMode) {
            this.applyTreeSearch();
        } else {
            this.filteredListItems = [...this.listItems];
        }
    }

    private applyTreeSearch(): void {
        if (!this.searchTerm.trim() && !this.postsOnlyFilter) {
            this.treeDataSource.data = this.orgTreeData;
            this.expandToLevel(this.orgTreeData, 2);
            return;
        }
        const words = this.searchTerm.trim()
            ? this.searchTerm
                  .toLowerCase()
                  .split(/\s+/)
                  .filter((w) => w.length > 0)
            : [];
        let filtered = words.length > 0 ? this.filterTreeNodes(this.orgTreeData, words) : this.orgTreeData;
        if (this.postsOnlyFilter) {
            filtered = this.filterPostsOnly(filtered);
        }
        this.treeDataSource.data = filtered;
        this.expandAllNodes(filtered);
    }

    /** Filter tree to only show post nodes (and their ancestor containers) */
    private filterPostsOnly(nodes: SelectorTreeNode[]): SelectorTreeNode[] {
        return nodes
            .map((node) => {
                if (node.partType === 'post') return { ...node };
                const filteredChildren = this.filterPostsOnly(node.children);
                if (filteredChildren.length > 0) {
                    return { ...node, children: filteredChildren };
                }
                return null;
            })
            .filter((node): node is SelectorTreeNode => node !== null);
    }

    private filterTreeNodes(nodes: SelectorTreeNode[], words: string[]): SelectorTreeNode[] {
        return nodes
            .map((node) => {
                const searchable = [
                    node.name.toLowerCase(),
                    node.partType.toLowerCase(),
                    ...(node.isHatNode ? (node.securityGroupNames || []).map((sg) => sg.toLowerCase()) : []),
                ].join(' ');
                const matches = this.fuzzyMatch(searchable, words);
                const filteredChildren = this.filterTreeNodes(node.children, words);

                if (matches || filteredChildren.length > 0) {
                    return { ...node, children: matches ? node.children : filteredChildren };
                }
                return null;
            })
            .filter((node): node is SelectorTreeNode => node !== null);
    }

    private expandAllNodes(nodes: SelectorTreeNode[]): void {
        for (const node of nodes) {
            this.treeControl.expand(node);
            if (node.children.length > 0) {
                this.expandAllNodes(node.children);
            }
        }
    }

    private applyListSearch(): void {
        if (!this.searchTerm.trim()) {
            this.filteredListItems = [...this.listItems];
            return;
        }
        const words = this.searchTerm
            .toLowerCase()
            .split(/\s+/)
            .filter((w) => w.length > 0);
        this.filteredListItems = this.listItems.filter((item) => {
            const searchable = `${item.name} ${item.subtitle}`.toLowerCase();
            return this.fuzzyMatch(searchable, words);
        });
    }

    /**
     * Multi-word fuzzy match: every word in the search must appear
     * somewhere in the searchable text. e.g. "fin dep" matches "Finance Department".
     */
    private fuzzyMatch(text: string, words: string[]): boolean {
        return words.every((word) => text.includes(word));
    }

    // ═══════════════ Summary ═══════════════

    private updateCounts(): void {
        if (this.isTreeMode) {
            this.selectedCount = this.countSelectedTreeNodes();
        } else {
            this.selectedCount = this.listItems.filter((i) => i.isSelected).length;
        }
    }

    // ═══════════════ Dialog Actions ═══════════════

    public confirm(): void {
        let selectedKeys: string[];
        let selectedItems: DisplayValue[];

        if (this.isTreeMode) {
            if (this.data.type === 'positions-with-hats') {
                // Collect position and hat selections separately
                const posKeys: string[] = [];
                const posItems: DisplayValue[] = [];
                const hatKeys: string[] = [];
                const hatItems: DisplayValue[] = [];
                this.collectPositionAndHatSelections(this.orgTreeData, posKeys, posItems, hatKeys, hatItems);

                // Also include selected position-free hats
                for (const hat of this.positionFreeHats) {
                    if (hat.isSelected) {
                        hatKeys.push(hat.key);
                        hatItems.push({ display: hat.name, value: hat.key });
                    }
                }

                // Return combined keys in selectedKeys, but store hat info in permissionSelections
                // (repurposing this field to pass hat selections)
                selectedKeys = posKeys;
                selectedItems = posItems;

                const result: ItemSelectorDialogResult = {
                    selectedKeys,
                    selectedItems,
                    permissionSelections: {
                        _hatKeys: hatKeys,
                        _hatNames: hatItems.map((h) => h.display),
                    },
                };
                this.dialogRef.close(result);
                return;
            } else {
                selectedKeys = this.getSelectedTreeKeys();
                selectedItems = [];
                this.collectDisplayValues(this.orgTreeData, selectedItems);
            }
        } else {
            const selected = this.listItems.filter((i) => i.isSelected);
            selectedKeys = selected.map((i) => i.key);
            selectedItems = selected.map((i) => ({ display: i.name, value: i.key }));
        }

        // Collect permission selections for features type
        let permissionSelections: Record<string, string[]> | undefined;
        if (this.data.type === 'features') {
            permissionSelections = {};
            const selected = this.listItems.filter((i) => i.isSelected);
            for (const item of selected) {
                const feat = item.features?.[0];
                if (feat?.selectedPermissions && feat.selectedPermissions.length > 0) {
                    permissionSelections[item.key] = [...feat.selectedPermissions];
                }
            }
        }

        const result: ItemSelectorDialogResult = { selectedKeys, selectedItems, permissionSelections };
        this.dialogRef.close(result);
    }

    /**
     * Collect position and hat selections from the tree into separate arrays.
     */
    private collectPositionAndHatSelections(
        nodes: SelectorTreeNode[],
        posKeys: string[],
        posItems: DisplayValue[],
        hatKeys: string[],
        hatItems: DisplayValue[]
    ): void {
        for (const node of nodes) {
            if (node.isSelected) {
                if (node.isHatNode) {
                    hatKeys.push(node.key);
                    hatItems.push({ display: node.name, value: node.key });
                } else {
                    posKeys.push(node.key);
                    posItems.push({ display: node.name, value: node.key });
                }
            }
            if (node.children.length > 0) {
                this.collectPositionAndHatSelections(node.children, posKeys, posItems, hatKeys, hatItems);
            }
        }
    }

    private collectDisplayValues(nodes: SelectorTreeNode[], items: DisplayValue[]): void {
        for (const node of nodes) {
            if (node.isSelected) {
                items.push({ display: node.name, value: node.key });
            }
            if (node.children.length > 0) {
                this.collectDisplayValues(node.children, items);
            }
        }
    }

    public cancel(): void {
        this.dialogRef.close(null);
    }

    // ═══════════════ Template Helpers ═══════════════

    public get isTreeMode(): boolean {
        return this.data.type === 'positions' || this.data.type === 'positions-with-hats';
    }

    /** Check if we're in positions-with-hats mode */
    public get isPositionsWithHatsMode(): boolean {
        return this.data.type === 'positions-with-hats';
    }
}
